/**
 * Why a scanner exists beside `JSON.parse`.
 *
 * `JSON.parse` is the only thing that may produce the VALUE — spec-exact
 * numbers, escapes and surrogate pairs are not worth re-deriving, and a
 * hand-written parser would drift from the engine the first time either changed.
 * But its `SyntaxError` cannot be shown to an operator: the message is English,
 * which U8 forbids shipping, and it carries **no position at all** for the two
 * commonest failures (an empty document, an unexpected token). Probed on V8:
 *
 *   JSON.parse('{"a": }')     → Unexpected token '}', … is not valid JSON   (no position)
 *   JSON.parse('')            → Unexpected end of JSON input                (no position)
 *   JSON.parse('{"a":1,\n"b" 2}') → … at position 14 (line 2 column 6)
 *
 * So the engine owns the value and this scanner owns the *location*: it runs
 * only to classify a failure the engine already reported, or to collect the
 * duplicate-key warnings the engine silently swallows. Every code is an i18n key
 * suffix, so nothing English ever reaches a screen.
 */

/** What went wrong, as a catalogue key suffix (`json.reason.<code>`). */
export type JsonParseCode =
  | 'unexpectedEnd'
  | 'unexpectedToken'
  | 'unterminatedString'
  | 'controlCharacter'
  | 'invalidEscape'
  | 'invalidNumber'
  | 'expectedColon'
  | 'expectedComma'
  | 'expectedPropertyName'
  | 'trailingComma'
  | 'trailingContent'
  /** The engine refused and the scanner could not say where. Positionless copy. */
  | 'unknown';

/** A path into a JSON document: object keys and array indices, outermost first. */
export type JsonPath = readonly (string | number)[];

export interface JsonDuplicateKey {
  /** The path of the OBJECT holding the repeat, not of the member. */
  path: JsonPath;
  key: string;
  /** The offset of the repeated key's opening quote. */
  offset: number;
}

export type JsonParseResult =
  | { ok: true; value: unknown; warnings: readonly JsonDuplicateKey[] }
  | { ok: false; code: JsonParseCode; offset: number | null };

/**
 * How deep the scanner will descend.
 *
 * It is recursive and V8's parser is not — the engine swallows 200_000 nested
 * arrays without blinking — so an unguarded scanner would be the one thing that
 * crashes on a document that parses. Past this depth it gives up cleanly: no
 * warnings, no position, never an exception. A settings value is two or three
 * levels deep; 1000 is "no honest document is down here".
 */
const MAX_SCAN_DEPTH = 1000;

interface ScanFailure {
  code: JsonParseCode;
  offset: number;
}

/** `null` = the scan completed and found nothing wrong. */
type ScanOutcome =
  | { available: true; failure: ScanFailure | null; duplicates: JsonDuplicateKey[] }
  | { available: false };

/** Thrown internally to unwind the descent; never escapes this module. */
class ScanAbort extends Error {
  constructor(readonly failure: ScanFailure | null) {
    super('scan-abort');
  }
}

const WHITESPACE = new Set([' ', '\t', '\n', '\r']);
const DIGITS = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']);
const HEX = new Set([...DIGITS, 'a', 'b', 'c', 'd', 'e', 'f', 'A', 'B', 'C', 'D', 'E', 'F']);
const SIMPLE_ESCAPES = new Set(['"', '\\', '/', 'b', 'f', 'n', 'r', 't']);

/** Joins a path into a Map key that a dotted object key cannot collide with. */
function pathKey(path: JsonPath): string {
  return path.map(String).join(' ');
}

/**
 * One pass over the text, recording every failure, every duplicate key and the
 * start offset of every value node.
 *
 * Written as a closure over a cursor rather than a class: it is one traversal
 * with one piece of state, and the alternative is threading an index through
 * nine mutually recursive functions.
 */
function scan(text: string): ScanOutcome & { offsets?: Map<string, number> } {
  let index = 0;
  const duplicates: JsonDuplicateKey[] = [];
  const offsets = new Map<string, number>();

  const abort = (code: JsonParseCode, offset: number): never => {
    throw new ScanAbort({ code, offset });
  };

  const skipWhitespace = (): void => {
    while (index < text.length && WHITESPACE.has(text[index] ?? '')) index += 1;
  };

  const expectEnd = (): never | void => {
    if (index >= text.length) abort('unexpectedEnd', text.length);
  };

  /** Consumes a string literal and returns its decoded-enough-to-compare content. */
  const scanString = (): string => {
    const start = index;
    index += 1; // the opening quote
    let out = '';

    for (;;) {
      if (index >= text.length) abort('unterminatedString', start);

      const char = text[index] ?? '';

      if (char === '"') {
        index += 1;
        return out;
      }

      if (char === '\\') {
        const escapeAt = index;
        const next = text[index + 1] ?? '';

        if (next === 'u') {
          const hex = text.slice(index + 2, index + 6);
          if (hex.length < 4 || [...hex].some((digit) => !HEX.has(digit))) {
            abort('invalidEscape', escapeAt);
          }
          out += String.fromCharCode(Number.parseInt(hex, 16));
          index += 6;
          continue;
        }

        if (!SIMPLE_ESCAPES.has(next)) abort('invalidEscape', escapeAt);
        out += next;
        index += 2;
        continue;
      }

      // A raw control character is its own failure, not an unterminated string:
      // an operator who pressed Enter inside a quoted value needs to be told
      // that this line break is the problem, not that the quote is missing.
      if (char < ' ') abort('controlCharacter', index);

      out += char;
      index += 1;
    }
  };

  const scanNumber = (): void => {
    const start = index;

    if (text[index] === '-') index += 1;

    if (text[index] === '0') {
      index += 1;
      // `01` is not a JSON number. Caught HERE rather than left to the caller,
      // which would otherwise report it as trailing content and send the
      // operator looking at the wrong character.
      if (DIGITS.has(text[index] ?? '')) abort('invalidNumber', start);
    } else if (DIGITS.has(text[index] ?? '')) {
      while (DIGITS.has(text[index] ?? '')) index += 1;
    } else {
      abort('invalidNumber', start);
    }

    if (text[index] === '.') {
      index += 1;
      if (!DIGITS.has(text[index] ?? '')) abort('invalidNumber', start);
      while (DIGITS.has(text[index] ?? '')) index += 1;
    }

    if (text[index] === 'e' || text[index] === 'E') {
      index += 1;
      if (text[index] === '+' || text[index] === '-') index += 1;
      if (!DIGITS.has(text[index] ?? '')) abort('invalidNumber', start);
      while (DIGITS.has(text[index] ?? '')) index += 1;
    }
  };

  const scanLiteral = (word: string): void => {
    const start = index;
    if (text.slice(index, index + word.length) !== word) abort('unexpectedToken', start);
    index += word.length;
  };

  const scanValue = (path: JsonPath, depth: number): void => {
    if (depth > MAX_SCAN_DEPTH) throw new ScanAbort(null);

    skipWhitespace();
    expectEnd();

    offsets.set(pathKey(path), index);
    const char = text[index] ?? '';

    if (char === '{') {
      scanObject(path, depth);
      return;
    }
    if (char === '[') {
      scanArray(path, depth);
      return;
    }
    if (char === '"') {
      scanString();
      return;
    }
    if (char === '-' || DIGITS.has(char)) {
      scanNumber();
      return;
    }
    if (char === 't') {
      scanLiteral('true');
      return;
    }
    if (char === 'f') {
      scanLiteral('false');
      return;
    }
    if (char === 'n') {
      scanLiteral('null');
      return;
    }

    abort('unexpectedToken', index);
  };

  const scanObject = (path: JsonPath, depth: number): void => {
    index += 1; // '{'
    const seen = new Set<string>();
    skipWhitespace();
    expectEnd();

    if (text[index] === '}') {
      index += 1;
      return;
    }

    for (;;) {
      skipWhitespace();
      expectEnd();

      if (text[index] !== '"') abort('expectedPropertyName', index);

      const keyAt = index;
      const key = scanString();
      if (seen.has(key)) duplicates.push({ path, key, offset: keyAt });
      seen.add(key);

      skipWhitespace();
      expectEnd();
      if (text[index] !== ':') abort('expectedColon', index);
      index += 1;

      scanValue([...path, key], depth + 1);

      skipWhitespace();
      expectEnd();

      if (text[index] === '}') {
        index += 1;
        return;
      }
      if (text[index] !== ',') abort('expectedComma', index);

      const commaAt = index;
      index += 1;
      skipWhitespace();
      expectEnd();
      // Reported at the COMMA, not at the brace that revealed it: the comma is
      // the character to delete.
      if (text[index] === '}') abort('trailingComma', commaAt);
    }
  };

  const scanArray = (path: JsonPath, depth: number): void => {
    index += 1; // '['
    let position = 0;
    skipWhitespace();
    expectEnd();

    if (text[index] === ']') {
      index += 1;
      return;
    }

    for (;;) {
      scanValue([...path, position], depth + 1);
      position += 1;

      skipWhitespace();
      expectEnd();

      if (text[index] === ']') {
        index += 1;
        return;
      }
      if (text[index] !== ',') abort('expectedComma', index);

      const commaAt = index;
      index += 1;
      skipWhitespace();
      expectEnd();
      if (text[index] === ']') abort('trailingComma', commaAt);
    }
  };

  try {
    scanValue([], 0);
    skipWhitespace();
    if (index < text.length) abort('trailingContent', index);

    return { available: true, failure: null, duplicates, offsets };
  } catch (error) {
    if (error instanceof ScanAbort) {
      // `failure === null` is the depth guard: the scan did not complete, so it
      // knows nothing — not even that the document is fine.
      return error.failure === null
        ? { available: false }
        : { available: true, failure: error.failure, duplicates, offsets };
    }
    throw error;
  }
}

/**
 * `JSON.parse`, with a failure an operator can act on and the duplicate keys the
 * engine throws away.
 *
 * Invalid text is never repaired and never guessed at — the caller keeps it on
 * screen and shows where it broke.
 */
export function parseJson(text: string): JsonParseResult {
  let value: unknown;

  try {
    value = JSON.parse(text);
  } catch {
    const outcome = scan(text);

    // The engine refused. If the scanner walked the whole document happily, the
    // two disagree — a spec divergence, or a limit only one of them has — and
    // the honest answer is a failure with NO position rather than a position
    // that would send an operator to a character that is fine.
    if (!outcome.available || outcome.failure === null) {
      return { ok: false, code: 'unknown', offset: null };
    }

    return { ok: false, code: outcome.failure.code, offset: outcome.failure.offset };
  }

  const outcome = scan(text);

  return {
    ok: true,
    value,
    warnings: outcome.available && outcome.failure === null ? outcome.duplicates : [],
  };
}

/**
 * Where a path's value starts in the text, so a schema issue can point at it.
 *
 * `null` when the path is not in the document, when the document does not parse,
 * or when it is deeper than the scanner walks — the caller draws no "go to"
 * affordance rather than a dead one.
 */
export function locatePath(text: string, path: JsonPath): number | null {
  const outcome = scan(text);

  if (!outcome.available || outcome.failure !== null || outcome.offsets === undefined) {
    return null;
  }

  return outcome.offsets.get(pathKey(path)) ?? null;
}
